import React, { PureComponent, Fragment } from 'react';
import PropTypes from 'prop-types';
import isEqual from 'lodash.isequal';
import { Formik } from 'formik';
import * as yup from 'yup';
import { Form, Button, Message, Responsive } from 'semantic-ui-react';
import { FormattedMessage, injectIntl, intlShape } from 'react-intl';
import { FormComponents } from '../components';
import { ApiStatuses } from '../api';
import { Formatters, Breakpoints } from '../utils';

const nonRequiredValue = '0';
const isLoadingOutOfBin = values =>
  values && values.PRIMARY_USE === 'LOADING_OUT_OF_BIN';

const placeholderMessageId = key => `compareform.${key}.placeholder`;
const labelMessageId = key => `compareform.${key}.label`;
const optionTextId = (selectKey, optionKey) =>
  `compareform.${selectKey}.options.${optionKey}`;

class FilterFormComponent extends PureComponent {
  state = { values: null };

  static propTypes = {
    intl: intlShape.isRequired,
    filterGroups: PropTypes.array,
    filterStatus: PropTypes.string,
    filterError: PropTypes.string,
    header: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    error: PropTypes.string,
    handleSubmit: PropTypes.func,
    submitting: PropTypes.bool,
    pristine: PropTypes.bool,
    invalid: PropTypes.bool,
    formValues: PropTypes.object,
    changeValue: PropTypes.func,
    onHandleSubmit: PropTypes.func
  };

  componentDidUpdate(prevProps, prevState) {
    const { values, setValues } = this.state;
    if (isEqual(prevState.values, values)) {
      return;
    }

    const { filterGroups } = this.props;
    let obj = {};
    filterGroups &&
      filterGroups.forEach(field => {
        // if the field is required, check if the current form value exists in the options
        // if it's removed in `mapState`, remove the value
        if (this.isRequired(field, values)) {
          values &&
            Object.keys(values).forEach(key => {
              if (key !== field.key) {
                return;
              }

              const match = (field.options || []).filter(
                option => option.key === values[key]
              );
              if (values[key] && !match.length) {
                obj[field.key] = '';
                setValues({ ...values, ...obj });
              }
            });

          // if the field is not required, force the value to be '0' zero in string
          // this is only for maxBinHeight so far, for BE Schema `maxBinHeight: String!`
          // if there'll be more non-required field need special treatment like this, BE should change Schema to just `String`
        } else {
          obj[field.key] = nonRequiredValue;
          setValues({ ...values, ...obj });
        }
      });
  }

  /**
   * AS-328: specific validations to dynamic fields > requirement changed in AS-641
   * when "loading out of bin"(LOADING_OUT_OF_BIN) under primary use (primaryUse),
   * should not have to pick a bin height (maxBinHeight)
   */
  isRequired = (field, values) => {
    const isBinHeightField = field.key === 'MAX_BIN_HEIGHT';
    const loadingOutOfBinSelected = isLoadingOutOfBin(values);
    return !(isBinHeightField && loadingOutOfBinSelected);
  };

  getValidators = (field, formValues, setValues) => {
    if (typeof formValues === 'function') {
      return true;
    }

    // TODO: this is not right, but there's no other way to access
    const values = { ...this.state.values, ...formValues };
    if (!isEqual(values, this.state.values)) {
      this.setState({
        values,
        setValues: setValues || this.state.setValues
      });
    }

    return this.isRequired(field, values);
  };

  setInitialValues = () => {
    const { filterGroups } = this.props;
    let groups = {};
    filterGroups &&
      filterGroups.forEach(group => {
        groups[group.key] = '';
      });
    return groups;
  };

  onHandleSubmit = (values, FormikBag) => {
    const { onHandleSubmit } = this.props;
    const { setFieldTouched, resetForm, setSubmitting } = FormikBag;
    setSubmitting(true);
    onHandleSubmit(values);
    Object.keys(values).forEach(key => {
      setFieldTouched(key, false);
    });
    // for visual submitting state
    setTimeout(() => {
      setSubmitting(false);
      resetForm(values);
    }, 1000);
  };

  validationSchema = () => {
    const { filterGroups } = this.props;
    let schema = {};

    filterGroups &&
      filterGroups.forEach(group => {
        // exclude non-required field
        if (this.isRequired(group, this.state.values)) {
          // DON'T SHOW VALIDATION MESSAGE - by design
          // because formik thinks it's validated if message is null, adding fake <output />
          schema[group.key] = yup.string().required(<output />);
        }
      });
    return yup.object().shape(schema);
  };

  render() {
    const { error, filterGroups, filterStatus, filterError, intl } = this.props;
    return (
      <Formik
        onSubmit={this.onHandleSubmit}
        validationSchema={this.validationSchema()}
        initialValues={this.setInitialValues()}
        enableReinitialize={true}
        isInitialValid={false}
      >
        {({
          handleSubmit,
          isSubmitting,
          handleChange,
          setValues,
          isValid,
          values
        }) => (
          <Form onSubmit={handleSubmit} error={error}>
            <div className="bg--secondary">
              <Form.Group className="fields--fluid">
                {filterStatus === ApiStatuses.Loaded && (
                  <Fragment>
                    {filterGroups &&
                      filterGroups.map(filterGroup => {
                        this.getValidators(filterGroup, values, setValues);
                        // disable the "Bin Height" field when the user selects "Loading Out of Bin"
                        const isDisabled =
                          isLoadingOutOfBin(values) &&
                          filterGroup.key === 'MAX_BIN_HEIGHT';
                        return (
                          <FormComponents.Select
                            name={filterGroup.key}
                            key={filterGroup.key}
                            disabled={isDisabled}
                            placeholder={intl.formatMessage({
                              id: placeholderMessageId(filterGroup.key),
                              defaultMessage: 'Select'
                            })}
                            placeholderDisabled={!isDisabled}
                            label={intl.formatMessage({
                              id: labelMessageId(filterGroup.key),
                              defaultMessage: filterGroup.value
                            })}
                            options={filterGroup.options.map(option => ({
                              value: option.key,
                              text: Formatters.toOptionText(
                                intl.formatMessage({
                                  id: optionTextId(filterGroup.key, option.key),
                                  defaultMessage: option.value
                                }),
                                option.value
                              )
                            }))}
                            onChange={handleChange(filterGroup.key)}
                          />
                        );
                      })}
                  </Fragment>
                )}
                {filterStatus === ApiStatuses.Error && (
                  <Message visible error content={filterError} icon="ban" />
                )}
                <FormComponents.FormError error={error} />
                {filterStatus === ApiStatuses.Loaded && (
                  <Form.Field
                    type="submit"
                    control={Button}
                    size="small"
                    primary
                    className="field--center field--flex"
                    data-qa="submit-button"
                    disabled={isSubmitting || !isValid}
                  >
                    <Responsive
                      as={Fragment}
                      maxWidth={Breakpoints.computerBreakpoint - 1}
                    >
                      <FormattedMessage
                        id="cta.search"
                        defaultMessage="Search"
                      />
                    </Responsive>
                    <Responsive
                      as={Fragment}
                      minWidth={Breakpoints.computerBreakpoint}
                    >
                      <FormattedMessage id="cta.go" defaultMessage="Go" />
                    </Responsive>
                  </Form.Field>
                )}
              </Form.Group>
            </div>
          </Form>
        )}
      </Formik>
    );
  }
}

export default injectIntl(FilterFormComponent);



// WEBPACK FOOTER //
// ./src/forms/FilterForm.js